<!-- TEMPLATE -->
<template>
  <div>
    <div class='row'>
      <div class='small-12 medium-12 large-12 hd-12 columns'>
        <dean-section-title content='true'>
            <span slot='title'>
              <span class='avenir-heavy'>{{title}}</span>
            </span>

          <dean-section-title-item
              :icon='deleteIcon'
              @click.native="terminatePartition()"
              v-if="showTerminatePartition()"
              :content="true"

          >
            VERWIJDER PARTITION
          </dean-section-title-item>

        </dean-section-title>

        <dean-two-column-layout>
          <div class="column" slot="left">
            <dean-section-divider>
              {{partitionTitle}}
            </dean-section-divider>

            <dean-table
              :data="partitionData"
              :columns="partitionColumns"
              :hover="true"
              @cell-click="clickToLogin"
              :showLayoutButtons="false">
            </dean-table>

            <dean-section-divider>
              {{servicesTitle}}
            </dean-section-divider>

            <dean-table
                :data="provisionedServices"
                :columns="provisionedServicesColumns"
                :showLayoutButtons="false">
            </dean-table>

            <template v-if="bundles.length > 0">
              <dean-section-divider>
                {{bundlesTitle}}
              </dean-section-divider>

              <dean-table
                  :data="bundles"
                  :columns="bundlesColumns"
                  :showLayoutButtons="false">
                <span slot="actions" slot-scope="actions">
                  <button
                    @click="decrementBundle(actions.value.content.productId)"
                    :disabled="actions.value.content.count <= 0 || !actions.value.content.canTerminate"
                    class="bundle-button">
                    -
                  </button>
                  <span class="bundle-count">{{ actions.value.content.count }}</span>
                  <button
                    @click="incrementBundle(actions.value.content.productId)"
                    :disabled="!actions.value.content.canProvision"
                    class="bundle-button">
                    +
                  </button>
                </span>
              </dean-table>
            </template>
          </div>

          <div class="column" slot="right">
            <dean-section-divider>
              {{phoneNumberRangeTitle}}
            </dean-section-divider>

            <dean-input-field
                id="phoneNumberRangeSearch"
                name="phoneNumberRangeSearch"
                v-model="phoneNumberRangeSearchQuery"
                :required-field="false"
                type="text">
              Search
            </dean-input-field>

            <dean-table
                :data="filteredPhoneNumberRanges"
                :columns="provisionedPhoneNumberRangesColumns"
                :hover="true"
                @cell-click="phoneNumberRangeCellClick"
                :showLayoutButtons="false">
              <span slot="terminate" slot-scope="terminate">
                <dean-icon v-if="terminate.value.content === true"
                           :glyph="terminateIcon"
                           width="16"
                           height="16"
                           title="Terminate">
                </dean-icon>
                <span v-else>
                  -
                </span>
              </span>
            </dean-table>

            <dean-section-divider>
              {{phoneNumberTitle}}
            </dean-section-divider>

            <dean-input-field
                id="phoneNumberSearch"
                name="phoneNumberSearch"
                v-model="phoneNumberSearchQuery"
                :required-field="false"
                type="text">
              Search
            </dean-input-field>

            <dean-table
                :data="filteredPhoneNumbers"
                :columns="provisionedPhoneNumbersColumns"
                :hover="true"
                @cell-click="phoneNumberCellClick"
                :showLayoutButtons="false">
              <span slot="terminate" slot-scope="terminate">
                <dean-icon v-if="terminate.value.content === true"
                           :glyph="terminateIcon"
                           width="16"
                           height="16"
                           title="Terminyate">
                </dean-icon>
                <span v-else>
                  -
                </span>
              </span>
            </dean-table>
          </div>
        </dean-two-column-layout>

        <transition name="fade" mode="out-in" v-if="isLoading">
          <dean-load-overlay class="table-overlay"></dean-load-overlay>
        </transition>
      </div>
    </div>
  </div>
</template>

<script lang='ts'>
// VUE
import {
  Component,
  Watch,
} from 'vue-property-decorator';
import axios from 'axios';
import deanTableMixin from '@/mixins/deanTableMixin';
import deanInputField from '@/components/deanComponents/deanInputField.vue';
import {mixins} from 'vue-class-component';
import sideMenuCustomerMixin from '@/mixins/sideMenuCustomerMixin';
import {CollapseTransition} from 'vue2-transitions';

import {
  // SVG
  featherIconX,
  deleteIcon,
  // COMPONENTS
  deanTable,
  deanPagination,
  deanInputSelect,
  deanSectionDivider,
  deanSectionTitle,
  deanSectionTitleItem,
  deanSearchField,
  deanIcon,
  deanLoadOverlay,
} from '@deanone/dean-components';
import deanNotification from '@/components/deanComponents/deanNotification.vue';
import DeanTwoColumnLayout from "@/components/deanComponents/deanTwoColumnLayout.vue";
import {getDutchDateFormat} from "@/mixins/dateWrapper";
import {deanDialogBus} from "@/main";
import {getErrorFromResponse} from '@/mixins/errorMessage';
import {authUserHasPrivilegeAndAction} from '@/mixins/privileges';
import {UserPrivilege} from '@/enums/UserPrivilege';
import {UserAction} from '@/enums/UserAction';

// interfaces
interface Partition {
  partitionId: string;
  partitionName: string;
  serviceProviderName: string;
  domain: string | null;
  adminEmail: string | null;
  creationDate: string;
  status: string;
  pbxGroupName: string | null;
  pbxGroupPhoneNumber: string | null;
}

interface PhoneNumberRange {
  from: string;
  to: string;
  orderId: string|null;
  orderNumber: string|null;
  orderLineId: string|null;
  activated: string;
  assigned: string;
  terminate: boolean;
}

interface PhoneNumber {
  number: string;
  orderId: string|null;
  orderNumber: string|null;
  orderLineId: string|null;
  activated: string;
  assigned: string;
  terminate: boolean;
}

interface Service {
  commercialName: string|null;
  name: string;
  quantity: number;
}

interface Bundle {
  productId: string;
  productName: string;
  count: number;
}

interface TableRow {
  label: string;
  value: any;
}

@Component({
  components: {
    DeanTwoColumnLayout,
    deanInputField,
    deanPagination,
    deanTable,
    deanInputSelect,
    deanSectionDivider,
    deanSectionTitle,
    deanSectionTitleItem,
    deanSearchField,
    deanIcon,
    deanNotification,
    deanLoadOverlay,
    CollapseTransition,
    featherIconX,
  },
})

export default class Details extends mixins(deanTableMixin, sideMenuCustomerMixin) {
  public title: string = 'Cloud PBX';
  public partitionTitle: string = 'Partitie details';
  public phoneNumberRangeTitle: string = 'Bereik van opgegeven telefoonnummers';
  public phoneNumberTitle: string = 'Telefoonnummers';
  public servicesTitle: string = 'Geleverde diensten';
  public bundlesTitle: string = 'Bel Bundels';
  public phoneNumberSearchQuery: string = '';
  public phoneNumberRangeSearchQuery: string = '';
  public activeCloudAndSipTrunkOrderLinesCount: number = 0;
  public partitionId: string = '';
  public isLoading: boolean = false;
  public terminateIcon: string = featherIconX;
  public deleteIcon: object = deleteIcon;

  private partitionDetails: Partition= {
    partitionId: '',
    partitionName: '',
    domain: '',
    adminEmail: '',
    serviceProviderName: '',
    creationDate: '',
    status: '',
    pbxGroupName: null,
    pbxGroupPhoneNumber: null,
  };
  private partitionData: Array<TableRow> = [];
  public partitionColumns: { [p: string]: object } = {
    label: {
      content: 'Gegeven',
      align: 'left',
    },
    value: {
      content: 'Waarde',
      align: 'left',
    },
  };

  private provisionedPhoneNumberRanges: Array<PhoneNumberRange> = [];
  private filteredPhoneNumberRanges: Array<PhoneNumberRange> = [];
  public provisionedPhoneNumberRangesColumns: { [p: string]: object } = {
    from: {
      content: 'Bereik van',
      align: 'left',
    },
    to: {
      content: 'Bereik tot',
      align: 'left',
    },
    activated: {
      content: 'Geactiveerd',
      align: 'left',
    },
    assigned: {
      content: 'Toegewezen',
      align: 'left',
    },
    orderNumber: {
      content: 'Order',
      align: 'left',
    },
    terminate: {
      content: 'Terminate',
      align: 'center',
      type: 'slot',
      slot: 'terminate',
    },
  };

  private provisionedPhoneNumbers: Array<PhoneNumber> = [];
  private filteredPhoneNumbers: Array<PhoneNumber> = [];
  public provisionedPhoneNumbersColumns: { [p: string]: object } = {
    number: {
      content: 'Telefoonnummer',
      align: 'left',
    },
    activated: {
      content: 'Geactiveerd',
      align: 'left',
    },
    assigned: {
      content: 'Toegewezen',
      align: 'left',
    },
    orderNumber: {
      content: 'Order',
      align: 'left',
    },
    terminate: {
      content: 'Terminate',
      align: 'center',
      type: 'slot',
      slot: 'terminate',
    },
  };

  private provisionedServices: Array<Service> = [];
  public provisionedServicesColumns: { [p: string]: object } = {
    commercialName: {
      content: 'Dienst',
      align: 'left',
    },
    name: {
      content: 'Naam',
      align: 'left',
    },
    quantity: {
      content: 'Aantal',
      align: 'center',
    },
  };

  private bundles: Array<Bundle> = [];
  public bundlesColumns: { [p: string]: object } = {
    productName: {
      content: 'Bundel',
      align: 'left',
    },
    actions: {
      content: 'Aantal',
      align: 'center',
      type: 'slot',
      slot: 'actions',
    },
  };

  // METHODS
  private async fetchPartitionDetails() {
    this.$store.dispatch('setLoadOverlay', true);

    // reset variables
    this.partitionData = [];
    this.provisionedPhoneNumberRanges = [];
    this.filteredPhoneNumberRanges = [];
    this.provisionedPhoneNumbers = [];
    this.filteredPhoneNumbers = [];
    this.provisionedServices = [];
    this.bundles = [];

    const customerInfoUrl = '/-/trade/api/customer.info';
    const partitionInfoUrl = '/-/trade/api/provisioning.partition.info';

    try {
      const customerInfoResponse = await axios.post(customerInfoUrl, {
        customerId: this.$route.params.customerId,
      });

      this.partitionId = customerInfoResponse.data.partitionId;
      this.activeCloudAndSipTrunkOrderLinesCount = customerInfoResponse.data.activeCloudAndSipTrunkOrderLinesCount;

      if (this.partitionId) {
        const partitionInfoResponse = await axios.post(partitionInfoUrl, {
          partitionId: this.partitionId,
        });

        this.partitionDetails.partitionId = partitionInfoResponse.data.identifier;
        this.partitionDetails.partitionName = partitionInfoResponse.data.partitionName;
        this.partitionDetails.serviceProviderName = partitionInfoResponse.data.serviceProviderName;
        this.partitionDetails.domain = partitionInfoResponse.data.domain;
        this.partitionDetails.adminEmail = partitionInfoResponse.data.PBXGroup?.groupAdminEmail;
        this.partitionDetails.status = partitionInfoResponse.data.status;
        this.partitionDetails.creationDate = partitionInfoResponse.data.creationDate;
        this.partitionDetails.pbxGroupName = partitionInfoResponse.data.PBXGroup?.id;
        this.partitionDetails.pbxGroupPhoneNumber = partitionInfoResponse.data.PBXGroup?.groupPhoneNumber;

        this.pushToPartitionData(this.partitionDetails);

        const provisionedPhoneNumberRanges = partitionInfoResponse.data.PBXGroup?.provisionedPhoneNumberRanges;

        if (provisionedPhoneNumberRanges) {
          this.filteredPhoneNumberRanges = this.provisionedPhoneNumberRanges = this.provisionedPhoneNumberRangesFactory(provisionedPhoneNumberRanges);
        }

        const provisionedPhoneNumbers = partitionInfoResponse.data.PBXGroup?.provisionedPhoneNumbers;

        if (provisionedPhoneNumbers) {
          this.filteredPhoneNumbers = this.provisionedPhoneNumbers = this.provisionedPhoneNumbersFactory(provisionedPhoneNumbers);
        }

        const provisionedServices = partitionInfoResponse.data.PBXGroup?.provisionedServices;

        if (provisionedServices) {
          this.provisionedServices = this.provisionedServicesFactory(provisionedServices);
        }

        const bundles = partitionInfoResponse.data.PBXGroup?.bundles;

        if (bundles) {
          this.bundles = this.bundlesFactory(bundles);
        }
      }
    } catch (err) {
      this.$toast(this.$store.state.main.notification.messages.provisioningNotAvailable);
    } finally {
      this.$store.dispatch('setLoadOverlay', false);
    }
  }

  public terminatePartition() {
    deanDialogBus.$emit('open', 'TerminatePartitionDialog', {
      customerId: this.$route.params.customerId,
    });
  }

  public showTerminatePartition() {
    if (! this.$store.getters.getUser.privileges.includes('terminate-partition-customer')) {
      return false;
    }

    if (! this.partitionId) {
      return false;
    }

    return true;
  }

  public created() {
    this.fetchPartitionDetails();
    this.buildSideMenuCustomer(this.$route.params.customerId);
  }

  public mounted() {
    deanDialogBus.$on('saved', () => {
      this.fetchPartitionDetails();
    });
  }

  public destroyed() {
    deanDialogBus.$off('saved');
  }

  public terminatePhoneNumber(partitionId: string, phoneNumber: string) {
    deanDialogBus.$emit('open', 'TerminatePhoneNumberDialog', {
      partitionId: partitionId,
      phoneNumber: phoneNumber,
    });
  }

  public terminatePhoneNumberBlock(partitionId: string, phoneNumberRangeFrom: string, phoneNumberRangeTo: string) {
    deanDialogBus.$emit('open', 'TerminatePhoneNumberBlockDialog', {
      partitionId: partitionId,
      phoneNumberRangeFrom: phoneNumberRangeFrom,
      phoneNumberRangeTo: phoneNumberRangeTo,
    });
  }

  @Watch('phoneNumberSearchQuery')
  public searchPhoneNumbers(): void {
    this.filteredPhoneNumbers = this.provisionedPhoneNumbers.filter((phoneNumber: PhoneNumber) =>
      phoneNumber.number.includes(this.phoneNumberSearchQuery)
    );
  }

  @Watch('phoneNumberRangeSearchQuery')
  public searchPhoneNumberRanges(): void {
    this.filteredPhoneNumberRanges = this.provisionedPhoneNumberRanges.filter((phoneNumberRange: PhoneNumberRange) =>
      phoneNumberRange.from.includes(this.phoneNumberRangeSearchQuery)
      || phoneNumberRange.to.includes(this.phoneNumberRangeSearchQuery)
      || (
        phoneNumberRange.from <= this.phoneNumberRangeSearchQuery
        && phoneNumberRange.to >= this.phoneNumberRangeSearchQuery
      )
    );
  }

  private provisionedPhoneNumberRangesFactory(provisionedPhoneNumberRanges: Array<any>): Array<PhoneNumberRange> {
    let result: Array<PhoneNumberRange> = [];

    for (const phoneNumberRange of provisionedPhoneNumberRanges) {
      let provisionedPhoneNumberRange: PhoneNumberRange = {
        from: phoneNumberRange.from,
        to: phoneNumberRange.to,
        orderId: phoneNumberRange.orderId,
        orderLineId: phoneNumberRange.orderLineId,
        activated: phoneNumberRange.activated ? 'Ja' : 'Nee',
        assigned: phoneNumberRange.assigned ? 'Ja' : 'Nee',
        orderNumber: phoneNumberRange.orderNumber,
        terminate: (
            !phoneNumberRange.orderNumber
            && !phoneNumberRange.activated
            && authUserHasPrivilegeAndAction(UserPrivilege.PROVISIONING_PHONE_NUMBER_RANGE, UserAction.TERMINATE)
        ),
      };

      result.push(provisionedPhoneNumberRange);
    }

    return result;
  }

  private provisionedPhoneNumbersFactory(provisionedPhoneNumbers: Array<any>): Array<PhoneNumber> {
    let result: Array<PhoneNumber> = [];

    for (const phoneNumber of provisionedPhoneNumbers) {
      let provisionedPhoneNumber: PhoneNumber = {
        number: phoneNumber.phoneNumber,
        orderId: phoneNumber.orderId,
        orderLineId: phoneNumber.orderLineId,
        activated: phoneNumber.activated ? 'Ja' : 'Nee',
        assigned: phoneNumber.assigned ? 'Ja' : 'Nee',
        orderNumber: phoneNumber.orderNumber,
        terminate: (
            !phoneNumber.orderNumber
            && !phoneNumber.activated
            && authUserHasPrivilegeAndAction(UserPrivilege.PROVISIONING_PHONE_NUMBER, UserAction.TERMINATE)
        ),
      };

      result.push(provisionedPhoneNumber);
    }

    return result;
  }

  private provisionedServicesFactory(provisionedServices: Array<any>): Array<Service> {
    let result: Array<Service> = [];

    for (const service of provisionedServices) {
      let provisionedService: Service = {
        commercialName: service.commercialName,
        name: service.name,
        quantity: service.quantity,
      };

      result.push(provisionedService);
    }

    return result;
  }

  private bundlesFactory(bundles: Array<any>): Array<any> {
    let result: Array<any> = [];

    for (const bundle of bundles) {
      let bundleItem = {
        productId: bundle.productId,
        productName: bundle.productName,
        count: bundle.count,
        actions: {
          productId: bundle.productId,
          count: bundle.count,
          canProvision: authUserHasPrivilegeAndAction(UserPrivilege.PROVISIONING_BUNDLE, UserAction.PROVISION),
          canTerminate: authUserHasPrivilegeAndAction(UserPrivilege.PROVISIONING_BUNDLE, UserAction.TERMINATE),
        },
      };

      result.push(bundleItem);
    }

    return result;
  }

  private pushToPartitionData(partitionDetails: Partition): void {
    this.partitionData = [];

    this.partitionData.push({
      label: 'ID',
      value: partitionDetails.partitionId,
    });

    this.partitionData.push({
      label: 'Partitie naam',
      value: partitionDetails.partitionName ? partitionDetails.partitionName + ' (inloggen)' : '-',
    });

    this.partitionData.push({
      label: 'Serviceprovider naam',
      value: partitionDetails.serviceProviderName,
    });

    this.partitionData.push({
      label: 'Domein',
      value: partitionDetails.domain,
    });

    this.partitionData.push({
      label: 'Admin e-mailadres',
      value: partitionDetails.adminEmail,
    });

    this.partitionData.push({
      label: 'Aanmaakdatum',
      value: getDutchDateFormat(partitionDetails.creationDate),
    });

    this.partitionData.push({
      label: 'Status',
      value: partitionDetails.status,
    });

    this.partitionData.push({
      label: 'PBX-groep naam',
      value: partitionDetails.pbxGroupName,
    });

    this.partitionData.push({
      label: 'PBX-groep telefoonnummer',
      value: partitionDetails.pbxGroupPhoneNumber,
    });
  }

  private phoneNumberRangeCellClick(target: any): void
  {
    if (target.row?.orderNumber) {
      this.$router.push({
        name: 'orders.orderline',
        params: {
          transactionId: target.row.orderId,
          cpId: target.row.orderLineId,
        },
      });
    } else if (
        true === target.row?.terminate
        && target.row?.from
        && target.row?.to
    ) {
      this.terminatePhoneNumberBlock(
          this.partitionDetails.partitionId,
          target.row.from,
          target.row.to
      );
    }
  }

  private phoneNumberCellClick(target: any): void
  {
    if (target.row?.orderNumber) {
      this.$router.push({
        name: 'orders.orderline',
        params: {
          transactionId: target.row.orderId,
          cpId: target.row.orderLineId,
        },
      });
    } else if (
        true === target.row?.terminate
        && target.row?.number
    ) {
      this.terminatePhoneNumber(
          this.partitionDetails.partitionId,
          target.row.number
      );
    }
  }

  private clickToLogin(target: any): void
  {
    if (target.row.label === 'Partitie naam') {
      this.isLoading = true;

      axios.post('/-/trade/api/customer.application.clicktologin', {
        username: this.partitionDetails.partitionName,
        customerId: this.$route.params.customerId,
      }).then(
          (response) => {
            window.open(response.data.data.url, '_blank');
          },
          (error) => {
            if (error.response.data?.errors?.length) {
              this.$toast({
                text: 'Inloggen mislukt, de server antwoordde met de volgende foutmelding: ('
                    + error.response.status + ') '
                    + error.response.data.errors[0].message,
                theme: 'danger',
              });
            }
          },
      ).finally(
          () => {
            this.isLoading = false;
          },
      );
    }
  }

  private async incrementBundle(productId: string): Promise<void> {
    this.isLoading = true;

    try {
      await axios.post('/-/trade/api/provisioning.provision.bundle', {
        customerId: this.$route.params.customerId,
        productId: productId,
      });

      await this.fetchPartitionDetails();
    } catch (err: any) {
      const errorMessage = getErrorFromResponse(err.response.data, 'Bundel toevoegen mislukt');

      this.$toast({
        text: errorMessage,
        theme: 'danger',
      });
    } finally {
      this.isLoading = false;
    }
  }

  private async decrementBundle(productId: string): Promise<void> {
    this.isLoading = true;

    try {
      await axios.post('/-/trade/api/provisioning.terminate.bundle', {
        customerId: this.$route.params.customerId,
        productId: productId,
      });

      await this.fetchPartitionDetails();
    } catch (err) {
      this.$toast({
        text: 'Bundel verwijderen mislukt',
        theme: 'danger',
      });
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<!-- STYLE -->

<style lang='scss' scoped>
@import '~@deanone/dean-components/src/scss/vars';

.bundle-button {
  min-width: 30px;
  height: 30px;
  padding: 0 10px;
  margin: 0 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f0f0f0;
    border-color: #999;
  }

  &:active:not(:disabled) {
    background-color: #e0e0e0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.bundle-count {
  display: inline-block;
  min-width: 30px;
  text-align: center;
  font-weight: bold;
}
</style>
